import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Banknote, Download, FileCheck, FileText, Plus, RotateCcw, ShieldCheck, XCircle,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";

import { useAuth } from "@/contexts/AuthContext";
import { InvoiceAPI, PaymentAPI, extractError } from "@/lib/api";
import { TaxBreakdownTable } from "@/components/TaxBreakdownTable";
import { InvoicePDFPreview } from "@/components/InvoicePDFPreview";
import { PaymentDialog } from "@/components/PaymentDialog";
import { GatewayCheckoutButton } from "@/components/GatewayCheckoutButton";

const inr = (m) => `₹${(Math.round((m || 0) / 100)).toLocaleString("en-IN")}`;
const STATUS = {
    draft: "border-zinc-300 bg-zinc-50 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
    issued: "border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
    partially_paid: "border-teal-300 bg-teal-50 text-teal-700 dark:bg-teal-950/40 dark:text-teal-200",
    paid: "border-emerald-300 bg-emerald-50 text-emerald-800 dark:bg-emerald-950/40 dark:text-emerald-200",
    overdue: "border-rose-300 bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-200",
    cancelled: "border-zinc-300 bg-zinc-100 text-zinc-600 line-through",
    refunded: "border-zinc-300 bg-zinc-100 text-zinc-600",
};

const useTitle = (label) => {
    useEffect(() => {
        document.title = `AutoBrix · ${label}`;
        return () => { document.title = "AutoBrix"; };
    }, [label]);
};

// ===== List =====
export default function InvoicesPage() {
    useTitle("Invoices");
    const { branches, permissions } = useAuth();
    const [items, setItems] = useState([]);
    const [status, setStatus] = useState("");
    const [branchId, setBranchId] = useState("");
    const [type, setType] = useState("");
    const [loading, setLoading] = useState(true);
    const [pdfBusyId, setPdfBusyId] = useState(null);
    const canCreate = permissions.includes("invoice:create");

    const downloadPdf = async (e, inv) => {
        e.stopPropagation();
        setPdfBusyId(inv.id);
        try {
            await InvoiceAPI.downloadPdf(inv.id, `${(inv.invoice_number || inv.id).replace(/\//g, "-")}.pdf`);
        } catch {
            toast.error("Failed to download PDF");
        } finally {
            setPdfBusyId(null);
        }
    };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await InvoiceAPI.list({
                status: status || undefined,
                branch_id: branchId || undefined,
                invoice_type: type || undefined,
                page_size: 200,
            });
            setItems(d.items || []);
        } finally { setLoading(false); }
    }, [status, branchId, type]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="invoices-page">
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">GST tax invoices</div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight">Invoices</h1>
                    <p className="mt-1 text-sm text-zinc-500">Gapless invoice numbering, CGST/SGST/IGST split by place of supply, credit notes on independent series.</p>
                </div>
                {canCreate && (
                    <Link to="/invoices/new"><Button data-testid="invoice-new-btn" className="bg-brand-red text-white hover:bg-brand-redDeep"><Plus className="mr-1 h-4 w-4" />New invoice</Button></Link>
                )}
            </header>

            <div className="flex flex-wrap items-center gap-2">
                <Select value={status} onValueChange={(v) => setStatus(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[180px]" data-testid="invoices-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All statuses</SelectItem>
                        {Object.keys(STATUS).map((s) => <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={branchId} onValueChange={(v) => setBranchId(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[220px]" data-testid="invoices-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All branches</SelectItem>
                        {(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.code} — {b.name}</SelectItem>)}
                    </SelectContent>
                </Select>
                <Select value={type} onValueChange={(v) => setType(v === "_all" ? "" : v)}>
                    <SelectTrigger className="w-[200px]" data-testid="invoices-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="_all">All types</SelectItem>
                        <SelectItem value="tax_invoice">Tax invoice</SelectItem>
                        <SelectItem value="credit_note">Credit note</SelectItem>
                    </SelectContent>
                </Select>
                <span className="font-mono text-xs text-zinc-500" data-testid="invoices-total-count">{loading ? "…" : `${items.length} invoices`}</span>
            </div>

            <div className="overflow-x-auto rounded-md border border-zinc-200 dark:border-zinc-800">
                <Table data-testid="invoices-table">
                    <TableHeader><TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Customer</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Due</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">PDF</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                        {items.map((i) => (
                            <TableRow key={i.id} data-testid={`invoice-row-${i.id}`} className="cursor-pointer"
                                       onClick={() => (window.location.href = `/invoices/${i.id}`)}>
                                <TableCell className="font-mono">{i.invoice_number}</TableCell>
                                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{(i.invoice_type || "tax_invoice").replace("_", " ")}</Badge></TableCell>
                                <TableCell>{i.customer_snapshot?.display_name || "—"}</TableCell>
                                <TableCell>
                                    <span data-testid={`invoice-status-pill-${i.status}`}
                                          className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-mono ${STATUS[i.status] || ""}`}>
                                        {i.status.replace(/_/g, " ")}
                                    </span>
                                </TableCell>
                                <TableCell className="text-right font-mono">{inr(i.total_minor)}</TableCell>
                                <TableCell className="text-right font-mono">{inr(i.amount_due_minor)}</TableCell>
                                <TableCell className="text-xs font-mono text-zinc-500">
                                    {i.invoice_date ? new Date(i.invoice_date).toLocaleDateString() : "—"}
                                </TableCell>
                                <TableCell className="text-right">
                                    {i.status === "draft" ? (
                                        <span className="text-xs text-zinc-400">—</span>
                                    ) : (
                                        <Button
                                            size="sm" variant="outline"
                                            data-testid={`invoice-pdf-download-${i.id}`}
                                            disabled={pdfBusyId === i.id}
                                            onClick={(e) => downloadPdf(e, i)}
                                        >
                                            <Download className="mr-1 h-3 w-3" />PDF
                                        </Button>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={8} className="py-12 text-center text-sm text-zinc-500">No invoices yet.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
        </div>
    );
}

// ===== Detail =====
export function InvoiceDetailPage() {
    useTitle("Invoice");
    const { id } = useParams();
    const { permissions } = useAuth();
    const [inv, setInv] = useState(null);
    const [cancelOpen, setCancelOpen] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    const [cnOpen, setCnOpen] = useState(false);
    const [cnReason, setCnReason] = useState("");

    const load = useCallback(async () => setInv(await InvoiceAPI.get(id)), [id]);
    useEffect(() => { load(); }, [load]);

    if (!inv) return <div className="py-12 text-center text-sm text-zinc-500">Loading…</div>;

    const canIssue = inv.status === "draft" && permissions.includes("invoice:issue");
    const canCancel = ["draft", "issued", "partially_paid"].includes(inv.status) && permissions.includes("invoice:cancel");
    const canCN = inv.status !== "draft" && inv.invoice_type !== "credit_note" && permissions.includes("invoice:credit_note");
    const canPay = ["issued", "partially_paid", "overdue"].includes(inv.status) && inv.amount_due_minor > 0 && permissions.includes("payment:create");
    const canShowGateway = inv.invoice_type !== "credit_note" && permissions.includes("payment:create");
    const canMargin = permissions.includes("invoice:read_margin");
    const isCN = inv.invoice_type === "credit_note";

    const issue = async () => {
        try { await InvoiceAPI.issue(id); toast.success("Issued"); load(); }
        catch (e) { toast.error(extractError(e)); }
    };
    const einvoice = async () => {
        try { const r = await InvoiceAPI.generateEinvoice(id); toast.success(`IRN: ${r.irn?.slice(0, 18)}…`); load(); }
        catch (e) { toast.error(extractError(e)); }
    };
    const cancel = async () => {
        if (!cancelReason) { toast.error("Reason required"); return; }
        try { await InvoiceAPI.cancel(id, cancelReason); toast.success("Cancelled"); setCancelOpen(false); load(); }
        catch (e) { toast.error(extractError(e)); }
    };
    const creditNote = async () => {
        try {
            const r = await InvoiceAPI.creditNote(id, cnReason || "Customer credit note");
            toast.success(`Credit note ${r.credit_note_number}`);
            setCnOpen(false);
            window.location.href = `/invoices/${r.credit_note_id}`;
        } catch (e) { toast.error(extractError(e)); }
    };

    const pdfUrl = InvoiceAPI.pdfUrl(id);

    return (
        <div className="space-y-6" data-testid="invoice-detail-page">
            <Link to="/invoices" className="inline-flex items-center text-sm text-zinc-500">
                <ArrowLeft className="mr-1 h-4 w-4" />Back
            </Link>
            <header className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-400">
                        {isCN ? "Credit note" : "Tax invoice"}
                    </div>
                    <h1 className="font-display text-3xl font-extrabold tracking-tight font-mono" data-testid="invoice-number-display">{inv.invoice_number}</h1>
                    <div className="mt-1 text-sm text-zinc-500">
                        {inv.customer_snapshot?.display_name} · {inv.branch_snapshot?.code} · v{inv.version}
                        {isCN && inv.parent_invoice_id && (
                            <> · Credit of <Link to={`/invoices/${inv.parent_invoice_id}`} className="text-brand-redDeep underline dark:text-brand-red">parent invoice</Link></>
                        )}
                    </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <span data-testid="invoice-status-pill"
                          className={`inline-flex rounded-md border px-3 py-1 text-sm font-mono ${STATUS[inv.status] || ""}`}>
                        <span data-testid={`invoice-status-pill-${inv.status}`}>{inv.status.replace(/_/g, " ")}</span>
                    </span>
                    {canIssue && (
                        <Button onClick={issue} data-testid="invoice-action-issue"
                                className="bg-brand-red text-white hover:bg-brand-redDeep">
                            <FileCheck className="mr-1 h-3.5 w-3.5" />Issue
                        </Button>
                    )}
                    {inv.status === "issued" && permissions.includes("invoice:issue") && (
                        <Button onClick={einvoice} variant="outline" size="sm" data-testid="invoice-action-einvoice">
                            <ShieldCheck className="mr-1 h-3.5 w-3.5" />
                            {inv.is_einvoice_generated ? "Re-generate e-invoice" : "Generate e-invoice"}
                            <Badge variant="outline" className="ml-2 font-mono text-[9px]"
                                   data-testid={`einvoice-mode-${inv.irn_is_stub ? "stub" : (inv.is_einvoice_generated ? "configured" : "off")}`}>
                                {inv.irn_is_stub ? "stub" : (inv.is_einvoice_generated ? "configured" : "off")}
                            </Badge>
                        </Button>
                    )}
                    <Button onClick={() => InvoiceAPI.downloadPdf(id, `${inv.invoice_number.replace(/\//g, "-")}.pdf`).catch(() => toast.error("Failed to download PDF"))}
                            variant="outline" size="sm" data-testid="invoice-action-pdf"
                            disabled={inv.status === "draft"}>
                        <FileText className="mr-1 h-3.5 w-3.5" />PDF
                    </Button>
                    {canPay && <PaymentDialog invoice={inv} onPaid={() => load()} triggerLabel="Record Payment" />}
                    {canShowGateway && <GatewayCheckoutButton invoice={inv} onCaptured={() => load()} />}
                    {canCancel && (
                        <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="invoice-action-cancel">
                                    <XCircle className="mr-1 h-3.5 w-3.5 text-rose-600" />Cancel
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Cancel invoice?</DialogTitle></DialogHeader>
                                <p className="text-xs text-zinc-500">Cancellation only allowed within 24 h of issue. The invoice number remains immutable.</p>
                                <Label>Reason</Label>
                                <Textarea rows={3} value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} data-testid="invoice-cancel-reason" />
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setCancelOpen(false)}>Back</Button>
                                    <Button className="bg-rose-600 text-white hover:bg-rose-700" onClick={cancel} data-testid="invoice-cancel-submit">Confirm cancel</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                    {canCN && (
                        <Dialog open={cnOpen} onOpenChange={setCnOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" size="sm" data-testid="invoice-action-credit-note">
                                    <RotateCcw className="mr-1 h-3.5 w-3.5" />Credit note
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader><DialogTitle>Create credit note?</DialogTitle></DialogHeader>
                                <p className="text-xs text-zinc-500">
                                    A new document on the <b>/CN/</b> series will be created. Parent invoice <b>{inv.invoice_number}</b> stays untouched.
                                </p>
                                <Label>Reason</Label>
                                <Textarea rows={3} value={cnReason} onChange={(e) => setCnReason(e.target.value)} data-testid="invoice-cn-reason" />
                                <DialogFooter>
                                    <Button variant="outline" onClick={() => setCnOpen(false)}>Back</Button>
                                    <Button className="bg-brand-red text-white hover:bg-brand-redDeep" onClick={creditNote} data-testid="invoice-cn-submit">Create credit note</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    )}
                </div>
            </header>

            <Tabs defaultValue="lines">
                <TabsList>
                    <TabsTrigger value="lines" data-testid="invoice-tab-lines">Lines</TabsTrigger>
                    <TabsTrigger value="tax" data-testid="invoice-tab-tax">Tax breakdown</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="invoice-tab-payments">Payments ({(inv.payments || []).length})</TabsTrigger>
                    <TabsTrigger value="pdf" data-testid="invoice-tab-pdf">PDF</TabsTrigger>
                </TabsList>

                <TabsContent value="lines">
                    <Card>
                        <CardContent className="p-0">
                            <Table data-testid="invoice-lines-table">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>#</TableHead>
                                        <TableHead>Description</TableHead>
                                        <TableHead>HSN/SAC</TableHead>
                                        <TableHead className="text-right">Qty</TableHead>
                                        <TableHead className="text-right">Rate</TableHead>
                                        <TableHead className="text-right">Taxable</TableHead>
                                        <TableHead className="text-right">Tax</TableHead>
                                        <TableHead className="text-right">Line total</TableHead>
                                        {canMargin && <TableHead className="text-right">Margin</TableHead>}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {(inv.lines || []).map((l) => (
                                        <TableRow key={l.id}>
                                            <TableCell className="font-mono">{l.line_no}</TableCell>
                                            <TableCell>{l.description}</TableCell>
                                            <TableCell className="font-mono text-xs">{l.hsn_sac_code}</TableCell>
                                            <TableCell className="text-right font-mono">{l.quantity}</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.unit_price_minor)}</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.taxable_amount_minor)}</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.tax_total_minor)} ({l.tax_rate_percent}%)</TableCell>
                                            <TableCell className="text-right font-mono">{inr(l.line_total_minor)}</TableCell>
                                            {canMargin && (
                                                <TableCell className="text-right font-mono" data-testid={`invoice-margin-cell-${l.id}`}>
                                                    {l.cost_minor != null ? inr(l.line_margin_minor || 0) : "—"}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <div className="space-y-1 border-t border-zinc-200 px-5 py-3 text-sm font-mono dark:border-zinc-800">
                                <Row label="Subtotal" value={inr(inv.subtotal_minor)} />
                                <Row label="Tax total" value={inr(inv.tax_total_minor)} />
                                <Row label="Grand total" value={<b>{inr(inv.total_minor)}</b>} />
                                <Row label="Paid" value={inr(inv.amount_paid_minor)} />
                                <Row label="Due" value={<b className="text-brand-redDeep dark:text-brand-red">{inr(inv.amount_due_minor)}</b>} />
                                {canMargin && (
                                    <Row label="Margin" value={<b data-testid="invoice-margin-cell">{inr(inv.margin_minor || 0)}</b>} />
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="tax">
                    <TaxBreakdownTable doc={inv} lines={inv.lines || []} testid="invoice-tax-breakdown" />
                    <Card className="mt-3">
                        <CardContent className="grid gap-2 p-4 text-xs font-mono sm:grid-cols-2">
                            <div><div className="text-zinc-500">Branch GSTIN</div><div data-testid="invoice-branch-gstin">{inv.branch_snapshot?.gstin || "—"}</div></div>
                            <div><div className="text-zinc-500">Customer GSTIN</div><div data-testid="invoice-customer-gstin">{inv.customer_snapshot?.gstin || "—"}</div></div>
                            <div><div className="text-zinc-500">Place of supply</div><div data-testid="invoice-place-of-supply">{inv.place_of_supply_state_code || "—"}</div></div>
                            <div><div className="text-zinc-500">IRN</div><div data-testid="invoice-irn">{inv.irn || "—"}{inv.irn_is_stub ? " · STUB" : ""}</div></div>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="payments">
                    <Card>
                        <CardContent className="p-0">
                            <Table data-testid="invoice-payments-table">
                                <TableHeader><TableRow>
                                    <TableHead>Payment #</TableHead>
                                    <TableHead>Method</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead className="text-right">Amount</TableHead>
                                    <TableHead>Received</TableHead>
                                </TableRow></TableHeader>
                                <TableBody>
                                    {(inv.payments || []).map((p) => (
                                        <TableRow key={p.id} data-testid={`invoice-payment-row-${p.id}`}>
                                            <TableCell className="font-mono">{p.payment_number || "—"}</TableCell>
                                            <TableCell>{p.method}</TableCell>
                                            <TableCell>{p.status}</TableCell>
                                            <TableCell className="text-right font-mono">{inr(p.amount_minor)}</TableCell>
                                            <TableCell className="text-xs font-mono text-zinc-500">
                                                {p.received_at ? new Date(p.received_at).toLocaleString() : "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(inv.payments || []).length === 0 && (
                                        <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-zinc-500">No payments yet.</TableCell></TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </CardContent>
                    </Card>
                </TabsContent>
                <TabsContent value="pdf">
                    <InvoicePDFPreview invoiceId={inv.id} status={inv.status} downloadName={`${inv.invoice_number.replace(/\//g, "-")}.pdf`} />
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-center justify-between border-b border-zinc-100 py-1.5 text-sm last:border-0 dark:border-zinc-800">
            <span className="text-zinc-500">{label}</span>
            <span>{value}</span>
        </div>
    );
}

// stash a no-op import so PaymentAPI hot-reload doesn't get tree-shaken
export const _payment_api_pin = PaymentAPI;
